import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../models';
import { PatientService, UserService, SocketService, ConfirmationService, ModalService, CaregiverService, NotificationService } from '../../services';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import { environment } from '../../../../environments/environment';
import { takeUntil, catchError, switchMap, finalize, debounceTime } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import {Subscription} from 'rxjs/Subscription';
import { VoipWorkstationService } from '../../../voip/services/voip-workstation.service';
import {SidebarService} from '../../services/sidebar.service';
import {ChatService} from '../../services/chat.service';
import { UsageTrackerService } from '../../services/usage-tracker.service';
import { readPage, toApiInstant } from '../../alarms-api';

interface NavigationItem {
  iconName: 'home' | 'hand-heart' | 'users-three' | 'projector-screen-chart' | 'chart-pie-slice' | 'bell' | 'phone' | 'chat';
  text: string;
  alt: string;
  route?: string;
  onClick: () => void;
  isActive?: boolean;
  badge?: number;
  event?: string;
}

@Component({
  selector: 'app-new-header',
  templateUrl: './new-header.component.html',
  styleUrls: ['./new-header.component.scss']
})
export class NewHeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public currentUser: User;
  public avatar;
  public type: string;
  public lang = '';
  public isLoading = false;
  public apiUrl;
  public gotEnableTraits;
  public gotEkoPrivilege: boolean = false;
  public gotEkoInterPrivilege: boolean = false;
  public gotEnableAiParamedic: boolean = false;
  public gotVoipEnabled: boolean = false;
  public managerGotAssignResponderPrivilege = false;
  public gotPrivilegeAssignResponder = false;
  public isSubmitting = false;
  public managerDetails = null;
  public responderTime: number = 10000;
  public intervalId: any;
  responders: any[] = [];
  telecaregivers: any[] = [];
  basicResponders: any[] = [];
  reserveResponders: any[] = [];
  public isCollapsed = false;
  public isLoggingOut = false;
  public isUserMenuOpen = false;
  public isLangMenuOpen = false;
  public isLogoutConfirmOpen = false;
  public langSubmenuPositioned = false;

  public availableLangs = [
    { code: 'pl', label: 'Polski' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'cs', label: 'Čeština' },
    { code: 'fr', label: 'Français' },
    { code: 'pt', label: 'Português' },
    { code: 'it', label: 'Italiano' },
    { code: 'ro', label: 'Română' },
    { code: 'da', label: 'Dansk' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'es', label: 'Español' },
    { code: 'ar', label: 'العربية' },
    { code: 'bg', label: 'Български' },
    { code: 'no', label: 'Norsk' },
    { code: 'sr', label: 'Српски' },
    { code: 'et', label: 'Eesti' },
    { code: 'lv', label: 'Latviešu' },
    { code: 'lt', label: 'Lietuvių' },
    { code: 'sl', label: 'Slovenščina' },
    { code: 'sk', label: 'Slovenčina' },
  ];

  @ViewChild('caregivers', { static: false }) caregivers: ElementRef;
  @ViewChild('users', { static: false }) users: ElementRef;
  @ViewChild('settings', { static: false }) settings: ElementRef;
  @ViewChild('caregiversArrow', { static: false }) caregiversArrow: ElementRef;
  @ViewChild('usersArrow', { static: false }) usersArrow: ElementRef;
  @ViewChild('settingsArrow', { static: false }) settingsArrow: ElementRef;
  @ViewChild('basicGroup', { static: false }) basicGroup: ElementRef;
  @ViewChild('basicGroupArrow', { static: false }) basicGroupArrow: ElementRef;
  @ViewChild('reserveGroup', { static: false }) reserveGroup: ElementRef;
  @ViewChild('reserveGroupArrow', { static: false }) reserveGroupArrow: ElementRef;
  @ViewChild('langBtnRef', { static: false }) langBtnRef: ElementRef;
  @ViewChild('langSubmenuRef', { static: false }) langSubmenuRef: ElementRef;

  public navigationItems: NavigationItem[] = [];

  private defaultNavigationItems = [...this.navigationItems];
  private routerSubscription!: Subscription;

  constructor(
    private userService: UserService,
    private patientService: PatientService,
    public router: Router,
    public translate: TranslateService,
    private socketService: SocketService,
    private modalService: ModalService,
    private confirmation: ConfirmationService,
    private caregiverService: CaregiverService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private sidebarService: SidebarService,
    private workstationService: VoipWorkstationService,
    public chatService: ChatService,   // public: szablon woła `toggleDockEnabled()` i czyta `dockEnabled`
    private usageTracker: UsageTrackerService
  ) {
    translate.addLangs(['en', 'pl', 'de', 'cs', 'fr', 'pt', 'it', 'ro', 'da', 'ar', 'bg', 'sr', 'no', 'nl', 'et', 'lv', 'lt', 'sl', 'sk', 'es']);

    if (window.localStorage['lang']) {
      translate.use(window.localStorage['lang']);
    } else {
      const browserLang = translate.getBrowserLang();
      translate.use(browserLang.match(/en|pl|de|cs|fr|pt|it|ro|da|ar|bg|sr|no|nl|ee|lv|sl|sk|es/) ? browserLang : 'en');
    }
    this.lang = window.localStorage['lang'];

    this.sidebarService.toggleSidebar$.subscribe(() => this.toggleSidebar());
    this.sidebarService.collapsed$.pipe(takeUntil(this.destroy$)).subscribe(isCollapsed => {
      this.isCollapsed = isCollapsed;
    });
    this.sidebarService.badgeRefresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.fetchUnhandledCount());
  }


  toggleSidebar() {
    this.sidebarService.setCollapsed(!this.isCollapsed);
  }

  // Pozycja „Czat" w menu konta — otwiera pełnoekranową historię czatu.
  openChatPage() {
    this.isUserMenuOpen = false;
    this.router.navigate(['/chat']);
  }

  /**
   * Nieprzeczytane wiadomości — badge przy pozycji „Czat" w menu konta.
   * Getter, a nie pole: `ChatService` utrzymuje tę liczbę na bieżąco (socket biegnie
   * przez `ngZone.run`, więc detekcja zmian i tak się odpala), a kopiowanie jej do pola
   * wymagałoby subskrypcji i sprzątania w `ngOnDestroy` bez żadnego zysku.
   */
  get chatUnread(): number {
    return this.chatService.unreadTotal;
  }

  ngOnInit() {
    this.apiUrl = environment.avatar_url;

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeNavigationItems();
    });

    this.userService.currentUser.pipe(takeUntil(this.destroy$)).subscribe(
      (userData) => {
        this.currentUser = userData;
        this.type = this.userService.getTypeOfUser();

        // Ustawienie uprawnień PRZED inicjalizacją nawigacji
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('enableTraits')) {
          this.gotEnableTraits = true;
        }

        // `user_id != undefined` jak w warunkach obok (162, 169, 172) — bez tego przy
        // wylogowaniu, gdy purgeAuth() wstawia do currentUser puste {}, leciał tu
        // TypeError: Cannot read properties of undefined (reading 'privilege').
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('eko') && !this.currentUser.user_id.privilege.includes('ekoInter')) {
          this.gotEkoPrivilege = true;
        }
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('ekoInter')) {
          this.gotEkoInterPrivilege = true;
        }
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('enableAiParamedic')) {
          this.gotEnableAiParamedic = true;
        }
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('voip')) {
          this.gotVoipEnabled = true;
        }
        if (this.currentUser.user_id != undefined && this.currentUser.user_id.privilege != undefined && this.currentUser.user_id.privilege.includes('assignResponder')) {
          this.gotPrivilegeAssignResponder = true;
        }
        else { this.getManagerDetails(); }

        // Inicjalizacja nawigacji PO ustawieniu uprawnień
        this.initializeNavigationItems();

        if (this.router.url.includes('indiv-client/add-user')) {
          this.navigationItems = this.navigationItems.filter(item => item.text === this.translate.instant('BACKEND.NAVIGATION-LOGOUT'));
        }

        this.routerSubscription = this.router.events.subscribe(event => {
          if (event instanceof NavigationEnd) {
            if (this.router.url.includes('indiv-client/add-user')) {
              this.navigationItems = this.navigationItems.filter(item => item.text === this.translate.instant('BACKEND.NAVIGATION-LOGOUT'));
            } else {
              this.navigationItems = [...this.defaultNavigationItems];
            }
            this.setActiveNavigationItem();
          }
        });

        if (Object.keys(userData).length > 0) {
          this.patientService.getAvatar(userData.user_id.id).subscribe(data => {
            this.avatar = data;
          });
        }

      }
    );

    this.setActiveNavigationItem();
    this.getResponderState();
    this.fetchUnhandledCount();

    // Każdy event `sendSignal` odpalał 3 żądania (~39 kB: responderState + pełne listy
    // alarmów i zdarzeń) tylko po to, żeby przeliczyć liczbę na dzwonku. Przy serii
    // eventów — np. kilku alarmach naraz — leciała seria takich trójek.
    // debounceTime zwija serię w jedno odświeżenie; sekunda opóźnienia badge'a jest
    // niezauważalna, a przy spokojnym ruchu zachowanie się nie zmienia.
    this.socketService.sendSignal().pipe(
      debounceTime(1000),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.getResponderState();
      this.fetchUnhandledCount();
    });


  }

  // Liczbę nieprzeczytanych liczy backend: pytamy o jeden wiersz i czytamy
  // `pagination.total`. Wcześniej trzeba było w tym celu ściągnąć DWIE pełne listy
  // (~39 kB) przy starcie i po każdym evencie socketu.
  private fetchUnhandledCount(): void {
    if (this.currentUser.role === 'ROLE_API_USER') return;

    let unseenAlarms = 0;
    let unseenNotifs = 0;

    const alarmsSince = toApiInstant(localStorage.getItem('dashboard_alarms_seen'));
    const notifsSince = toApiInstant(localStorage.getItem('dashboard_notifications_seen'));

    // Tylko nieobsłużone: bez filtra statusu znacznik bez `from` liczył całą historię
    // (ratowniczka telecentrum: 43 861), a z `from` — także obsłużone.
    this.patientService.findAlarms(alarmsSince
      ? { status: 'unhandled', from: alarmsSince, limit: 1 }
      : { status: 'unhandled', limit: 1 })
      .subscribe(response => {
        unseenAlarms = readPage(response).total;
        this.updateSidebarBadge(unseenAlarms + unseenNotifs);
      }, () => {});

    const notifsQuery = notifsSince
      ? { category: 'event', handled: false, from: notifsSince, limit: 1 }
      : { category: 'event', handled: false, limit: 1 };

    this.patientService.findNotifications(notifsQuery)
      .subscribe(response => {
        unseenNotifs = readPage(response).total;
        this.updateSidebarBadge(unseenAlarms + unseenNotifs);
      }, () => {});
  }

  private updateSidebarBadge(count: number): void {
    const notifItem = this.navigationItems.find(i => i.route === 'my-dashboard');
    if (notifItem) {
      notifItem.badge = count > 0 ? count : undefined;
    }
  }

  shouldActivateDropdown(): boolean {
    const url = this.router.url;
    return true;
  }

  private initializeNavigationItems(): void {
    const role = this.currentUser.role;

    if (this.currentUser.user_id == undefined) {
      this.navigationItems = [];
    } else if (role === 'ROLE_API_MANAGER') {
      const baseItems: NavigationItem[] = [
        {
          iconName: 'home',
          text: this.translate.instant('BACKEND.NAVIGATION-HOME'),
          alt: "Home icon",
          route: 'manager/dashboard',
          onClick: () => this.navigateTo('manager/dashboard'),
          event: 'EVENTS.MENU.HOME'
        },
        {
          iconName: 'hand-heart',
          text: this.translate.instant('BACKEND.NAVIGATION-CAREGIVERS'),
          alt: "Guardians icon",
          route: 'caregivers',
          onClick: () => this.navigateTo('caregivers'),
          event: 'EVENTS.MENU.CAREGIVERS'
        },
        {
          iconName: 'users-three',
          text: this.translate.instant('BACKEND.NAVIGATION-USERS'),
          alt: "Users icon",
          route: 'users',
          onClick: () => this.navigateTo('users'),
          event: 'EVENTS.MENU.USERS'
        },
        {
          iconName: 'projector-screen-chart',
          text: this.translate.instant('BACKEND.NAVIGATION-PROJECTS'),
          alt: "Projects icon",
          route: 'projects',
          onClick: () => this.navigateTo('projects'),
          event: 'EVENTS.MENU.PROJECTS'
        },
        {
          iconName: 'chart-pie-slice',
          text: this.translate.instant('BACKEND.NAVIGATION-REPORTS'),
          alt: "Reports icon",
          route: 'settings/reports',
          onClick: () => this.navigateTo('settings/reports'),
          event: 'EVENTS.MENU.REPORTS'
        },
        {
          iconName: 'bell',
          text: this.translate.instant('BACKEND.NAVIGATION-NOTIFICATIONS'),
          alt: "Notifications icon",
          route: 'my-dashboard',
          onClick: () => this.navigateTo('my-dashboard'),
          event: 'EVENTS.MENU.NOTIFICATIONS'
        }
      ];

      // Dodaj AI Paramedic jeśli manager ma uprawnienie
      if (this.gotEnableAiParamedic) {
        baseItems.push({
          iconName: 'phone',
          text: 'SiDLY Reach',
          alt: "SiDLY Reach icon",
          route: 'manager/ai-paramedic',
          onClick: () => this.navigateTo('manager/ai-paramedic'),
          event: 'EVENTS.MENU.AI-PARAMEDIC'
        });
      }

      // Dodaj VoIP Ustawienia i Historię jeśli manager ma uprawnienie (telefon jest jako floating widget)
      if (this.gotVoipEnabled) {
        baseItems.push({
          iconName: 'phone',
          text: this.translate.instant('VOIP.CONFIGURATION_TITLE'),
          alt: "VoIP Config icon",
          route: 'voip/config',
          onClick: () => this.navigateTo('voip/config'),
          event: 'EVENTS.MENU.VOIP-CONFIGURATION'
        });
        baseItems.push({
          iconName: 'chart-pie-slice',
          text: this.translate.instant('VOIP.HISTORY_TITLE'),
          alt: "VoIP History icon",
          route: 'voip/call-logs',
          onClick: () => this.navigateTo('voip/call-logs'),
          event: 'EVENTS.MENU.VOIP-HISTORY'
        });
      }

      this.navigationItems = baseItems;
    } else if (role === 'ROLE_API_CAREGIVER')  {
      // Ratownik telecentrum pracuje na liście alarmów — „Strona główna" prowadzi do zdarzeń
      const homeRoute = this.currentUser.telecentrum_mode ? 'my-dashboard' : 'caregivers/dashboard-c';
      const caregiverItems: NavigationItem[] = [
        {
          iconName: 'home',
          text: this.translate.instant('BACKEND.NAVIGATION-HOME'),
          alt: "Home icon",
          route: homeRoute,
          onClick: () => this.navigateTo(homeRoute),
          event: 'EVENTS.MENU.HOME'
        },
        {
          iconName: 'users-three',
          text: this.translate.instant('BACKEND.NAVIGATION-USERS'),
          alt: "Users icon",
          route: 'users',
          onClick: () => this.navigateTo('users'),
          event: 'EVENTS.MENU.USERS'
        },
      ];

      // Obserwator (ROLE_API_CAREGIVER + type 'Observer') nie ma dostępu do raportów
      if (this.type !== 'Observer') {
        caregiverItems.push({
          iconName: 'chart-pie-slice',
          text: this.translate.instant('BACKEND.NAVIGATION-REPORTS'),
          alt: "Reports icon",
          route: 'settings/reports',
          onClick: () => this.navigateTo('settings/reports'),
          event: 'EVENTS.MENU.REPORTS'
        });
      }

      if (this.currentUser.telecentrum_mode) {
        // „Strona główna" prowadzi już do zdarzeń — drugie „Powiadomienia" byłoby duplikatem,
        // a pulpit opiekuna zostałby bez wejścia z menu.
        caregiverItems.push({
          iconName: 'projector-screen-chart',
          text: this.translate.instant('BACKEND.NAVIGATION-DASHBOARD'),
          alt: "Dashboard icon",
          route: 'caregivers/dashboard-c',
          onClick: () => this.navigateTo('caregivers/dashboard-c'),
          event: 'EVENTS.MENU.DASHBOARD'
        });
      } else {
        caregiverItems.push({
          iconName: 'bell',
          text: this.translate.instant('BACKEND.NAVIGATION-NOTIFICATIONS'),
          alt: "Notifications icon",
          route: 'my-dashboard',
          onClick: () => this.navigateTo('my-dashboard'),
          event: 'EVENTS.MENU.NOTIFICATIONS'
        });
      }

      // VoIP dla caregivera jest dostępny przez floating widget (prawy dolny róg)

      this.navigationItems = caregiverItems;
    } else if (role === 'ROLE_API_ADMIN') {
      this.navigationItems = [
        {
          iconName: 'users-three',
          text: 'Managerowie',
          alt: 'Managers admin icon',
          route: 'admin/managers',
          onClick: () => this.navigateTo('admin/managers')
        },
        {
          iconName: 'phone',
          text: this.translate.instant('VOIP_BOARD_NAV'),
          alt: 'VoIP topology icon',
          route: 'admin/voip',
          onClick: () => this.navigateTo('admin/voip')
        },
        {
          iconName: 'chart-pie-slice',
          text: this.translate.instant('ADMIN_STATS.NAV'),
          alt: 'Statistics icon',
          route: 'admin/stats',
          onClick: () => this.navigateTo('admin/stats')
        }
      ];
    } else {
      const myCardRoute = this.userService.getMyPatientCardUrl().substring(1);
      const caregiverItems: NavigationItem[] = [
        {
          iconName: 'home',
          text: this.translate.instant('BACKEND.PROFILE-TITLE'),
          alt: "Home icon",
          route: myCardRoute,
          onClick: () => this.navigateTo(myCardRoute),
          event: 'EVENTS.MENU.PROFILE'
        },
      ];

      this.navigationItems = caregiverItems;
    }

    this.defaultNavigationItems = [...this.navigationItems];
  }



  getManagerDetails() {
    if (this.currentUser && this.currentUser.user_id && this.currentUser.user_id.id) {
      this.userService.getManagerPrivilege(this.currentUser.user_id.id).subscribe(
        response => {
          if (response) {
            this.managerDetails = response;
            if (this.managerDetails !== 'no-data' && this.managerDetails.data) {
              if (this.managerDetails.data.includes('assignResponder')) {
                this.managerGotAssignResponderPrivilege = true;
              }
            }
          }
        }, () => {}
      );
    }
  }


  responderEndIntervention(imei: string, firstName: string, lastName: string) {
    this.modalService
      .openAccept(this.translate.instant("USER.RESPONDER.ARE-YOU-SURE-TO-END-INTERVENTION", { firstName: firstName, lastName: lastName }), 'EVENTS.MENU.POPUP.USER-RESPONDER-ARE-YOU-SURE-TO-END-INTERVENTION')
      .subscribe((confirmation) => {
        if (confirmation.ok === true) {
          this.isSubmitting = true;

          this.userService.responderEndIntervention(imei).subscribe(
            () => {
              this.confirmation.show('USER.RESPONDER.INTERVENTION-ENDED', undefined, 'EVENTS.MENU.POPUP.USER-RESPONDER-INTERVENTION-ENDED');
              this.isSubmitting = false;
            },
            () => {
              this.modalService.open(this.translate.instant('USER.RESPONDER.ERROR-END-INTERVENTION'), undefined, undefined, 'EVENTS.MENU.POPUP.USER-RESPONDER-ERROR-END-INTERVENTION');
              this.isSubmitting = false;
            }
          );
        }
      });
  }

  getResponderState() {
    this.userService.getResponderState().subscribe(
      data => {
        this.responders = data.data;
        this.basicResponders = this.responders.filter(rs => rs.isResponderBasic === '1');
        this.reserveResponders = this.responders.filter(rs => rs.isResponderBasic === '0' || rs.isResponderBasic == null || rs.isResponderBasic === "---");

        if (this.responders.some(rs => rs.status === '1')) {
          this.startResponderInterval();
        }
      },
      () => { }
    );

  }

  startResponderInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      const uniqueAlarmIds = new Set(this.responders.filter(rs => rs.status === '1').map(rs => rs.alarmId));
      if (uniqueAlarmIds.size > 0) {
        uniqueAlarmIds.forEach(alarmId => {
          this.notificationService.sentActionsCheck(alarmId).subscribe(
            () => { },
            () => { }
          );
        });
      } else {
        clearInterval(this.intervalId);
      }
    }, this.responderTime);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public changeLanguage(lang: string): void {
    this.translate.use(lang);
    window.localStorage['lang'] = lang;
    this.lang = window.localStorage['lang'];
    location.reload();
  }

  logout() {
    this.isLoading = true;
    this.userService.isLoggingOut$.next(true);

    // Jeśli użytkownik jest zalogowany na stanowisko VOIP, najpierw poprawnie
    // zamknij sesję: zakończ ewentualną przerwę (zapis czasu zakończenia w bazie)
    // i wyloguj ze stanowiska (zapis „wylogowano" w historii VOIP), a dopiero
    // potem wyloguj z konta. Wszystko best-effort — błędy nie blokują wylogowania.
    this.workstationService.getMySession().pipe(
      catchError(() => of(null)),
      switchMap((session: any) => {
        // Zamykamy sesję stanowiska, chyba że backend jednoznacznie zwrócił jej brak.
        // Uwaga: /my-session nie zawsze zwraca pole hasSession — dlatego NIE wymagamy
        // hasSession === true (to gubiło zapis wylogowania/końca przerwy), a jedynie
        // pomijamy zamykanie, gdy sesji na pewno nie ma (hasSession === false).
        if (!session || session.hasSession === false) {
          return of(null); // brak sesji stanowiska — nic nie zamykamy
        }
        const endBreak$ = session.isOnBreak
          ? this.workstationService.endBreak().pipe(catchError(() => of(null)))
          : of(null);
        return endBreak$.pipe(
          switchMap(() => this.workstationService.logoutFromWorkstation().pipe(catchError(() => of(null))))
        );
      }),
      finalize(() => {
        // GET /user/logout unieważnia token, a paczka zdarzeń wysłana po nim już nie przejdzie.
        this.usageTracker.sendPending().then(() =>
          this.userService.logout().subscribe(
            () => this.finishLogout(),
            () => this.finishLogout()
          )
        );
      })
    ).subscribe();
  }

  private finishLogout() {
    this.userService.purgeAuth();
    this.router.navigateByUrl('/auth/login').then(() => {
      window.location.reload();
    });
  }

  setActiveNavigationItem() {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // bez query & fragment

    this.navigationItems.forEach(item => {
      item.isActive = !!item.route && (`/${item.route}` === currentRoute);
    });
  }

  navigateTo(path: string) {
    this.router.navigateByUrl('/' + path);
  }

  /**
   * Pozycja menu ma href, więc środkowy przycisk / Ctrl+klik / Shift+klik otwiera ją w nowej
   * karcie lub oknie (obsługuje to przeglądarka). Zwykłe kliknięcie — nawigacja aplikacji bez przeładowania.
   */
  onNavClick(event: MouseEvent, item: { route?: string, onClick: () => void }): void {
    if (item.route && (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0)) {
      return;
    }
    event.preventDefault();
    item.onClick();
  }

  goToDashboard() {
    if (!this.currentUser) {
      return;
    }

    const role = this.currentUser.role;

    if (role === 'ROLE_API_MANAGER') {
      this.navigateTo('manager/dashboard');
    } else if (role === 'ROLE_API_CAREGIVER') {
      this.navigateTo('caregivers/dashboard-c');
    } else if (role === 'ROLE_API_USER') {
      this.navigateTo('users/profile');
    } else {
      this.navigateTo('manager/dashboard');
    }
  }

  toggleDropdown(elem, arrow) {
    if (elem.classList.value.includes('active')) {
      elem.classList.remove('active');
      arrow.classList.remove('active');
    } else {
      elem.classList.add('active');
      arrow.classList.add('active');
    }
  }

  formatName(firstName: string, lastName: string): string {
    const maxLength = 18;
    let formattedName = `${lastName} ${firstName}`;

    if (formattedName.length > maxLength) {
      formattedName = formattedName.substr(0, maxLength - 3) + '...';
    }

    return formattedName;
  }

  goToConfiguration() {
    const role = this.currentUser.role;

    if (role === 'ROLE_API_CAREGIVER') {
      this.router.navigateByUrl('settings/configuration-c');
    } else if (role === 'ROLE_API_USER') {
      this.router.navigateByUrl(this.userService.getMyPatientSettingsUrl());
    } else {
      this.router.navigateByUrl('settings/configuration');
    }
  }

  getRoleLabel(): string {
    switch (this.currentUser?.role) {
      case 'ROLE_API_MANAGER': return this.translate.instant('HEADER.MANAGER');
      case 'ROLE_API_CAREGIVER': return this.translate.instant(this.type === 'Observer' ? 'HEADER.OBSERVER' : 'HEADER.CAREGIVER');
      case 'ROLE_API_USER': return this.translate.instant('HEADER.USER');
      default: return '';
    }
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (!this.isUserMenuOpen) {
      this.isLangMenuOpen = false;
    }
  }

  openLanguageMenu(): void {
    this.isLangMenuOpen = !this.isLangMenuOpen;
    if (this.isLangMenuOpen) {
      this.langSubmenuPositioned = false;
      // *ngIf montuje submenu dopiero po tym ticku — mierzymy jego realny rozmiar
      // po renderze, inaczej getBoundingClientRect() zwróciłby zera.
      setTimeout(() => this.positionLangSubmenu());
    }
  }

  /**
   * Fixed-position submenu językowe liczone względem okna, nie sztywnym CSS
   * ("otwórz zawsze w prawo od przycisku") — na węższym/przeskalowanym viewporcie
   * (np. laptop z niestandardowym skalowaniem Windows) sztywny flyout wylatywał
   * poza ekran i nie dało się kliknąć np. polskiego. Domyka submenu w widoczny obszar
   * z obu osi, z marginesem, i dopiero wtedy je pokazuje.
   */
  private positionLangSubmenu(): void {
    const btn = this.langBtnRef?.nativeElement;
    const menu = this.langSubmenuRef?.nativeElement;
    if (!btn || !menu) {
      return;
    }

    const margin = 8;
    const btnRect = btn.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    let left = btnRect.right + 4;
    if (left + menuRect.width > window.innerWidth - margin) {
      left = btnRect.left - menuRect.width - 4;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - menuRect.width - margin));

    let top = btnRect.bottom - menuRect.height;
    top = Math.max(margin, Math.min(top, window.innerHeight - menuRect.height - margin));

    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
    this.langSubmenuPositioned = true;
  }

  getLangLabel(): string {
    const found = this.availableLangs.find(l => l.code === this.lang);
    return found ? found.label : 'Polski';
  }

  selectLang(code: string): void {
    this.changeLanguage(code);
    this.isLangMenuOpen = false;
    this.isUserMenuOpen = false;
  }

  confirmLogout(): void {
    this.isLogoutConfirmOpen = true;
    this.isUserMenuOpen = false;
    this.isLangMenuOpen = false;
  }

  cancelLogout(): void {
    this.isLogoutConfirmOpen = false;
  }

}
